import {
  forwardRef,
  useId,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as LabelPrimitive from '@radix-ui/react-label';
import { Icon } from '../../components/ui/Icon';
import { cn } from '../../lib/cn';

/**
 * Dark-shell structural primitives for the Platform Console (Stage 4 Phase
 * 5). `DESIGN.md` locks TWO token systems — a light Tenant Shell and a dark
 * Platform Console (`platform-*`) — and every shared component in
 * `components/ui/` (`Card`, `KpiCard`, `Button`, `Field`, `DataTable`, the
 * light `Modal`) is hardcoded to the light tokens (`bg-surface`,
 * `border-line`, `text-ink`). Rather than parameterize every one of those
 * with a theme prop (touching 5 phases' worth of already-shipped, already-
 * verified light-shell pages for a phase that only needs 6 dark screens),
 * this is a parallel, small, dark-only set — mirroring the same shapes so
 * the two systems stay visually consistent, reusing anything token-agnostic
 * (`Icon`, `StatusPill`, `ComingSoonBadge` — all built on the shared
 * `positive`/`alert`/`accent` semantic tokens, which read correctly on
 * either background, confirmed against all 6 reference screens' rendered
 * pill usage during this phase's pre-work verification).
 */

export function PCard({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('rounded-card border border-platform-border bg-platform-surface shadow-card', className)}
      {...props}
    />
  );
}

export function PPageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: ReactNode;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4 border-b border-platform-border pb-4 lg:flex-row lg:items-center lg:justify-between">
      <div>
        {eyebrow && (
          <div className="mb-1 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-platform-muted">
            {eyebrow}
          </div>
        )}
        <h1 className="text-2xl font-bold tracking-tight text-platform-text">{title}</h1>
        {description && <p className="mt-1 max-w-3xl text-xs text-platform-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2.5">{actions}</div>}
    </div>
  );
}

export function PSpinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <span role="status" className="inline-flex items-center gap-2 text-sm text-platform-muted">
      <span
        aria-hidden
        className="h-4 w-4 animate-spin rounded-full border-2 border-platform-subtle border-r-transparent"
      />
      {label}
    </span>
  );
}

export function PEmptyState({ title, description }: { title: string; description?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-card border border-dashed border-platform-border bg-platform-card-muted px-6 py-12 text-center">
      <p className="font-medium text-platform-text">{title}</p>
      {description && <p className="max-w-sm text-sm text-platform-muted">{description}</p>}
    </div>
  );
}

export function PErrorNote({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="rounded border border-alert/30 bg-alert-soft px-3 py-2 text-sm text-alert">
      {children}
    </div>
  );
}

export function PFeatureStub({ title, description }: { title: string; description?: ReactNode }) {
  return (
    <div className="flex min-h-[160px] flex-col items-center justify-center gap-2 rounded-card border border-dashed border-platform-border bg-platform-card-muted px-6 py-10 text-center">
      <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-platform-surface text-platform-muted">
        <Icon name="upcoming" className="text-[20px]" />
      </div>
      <p className="font-medium text-platform-text">{title}</p>
      {description && <p className="max-w-sm text-sm text-platform-muted">{description}</p>}
      <PComingSoonBadge className="mt-1" />
    </div>
  );
}

export function PComingSoonBadge({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-platform-border bg-platform-card-muted px-2 py-0.5 text-[11px] font-medium text-platform-muted',
        className,
      )}
    >
      <Icon name="schedule" className="text-[12px]" />
      Coming soon
    </span>
  );
}

// ── Table ────────────────────────────────────────────────────────────────

export function PTable({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className="overflow-x-auto rounded-card border border-platform-border bg-platform-surface">
      <table className={cn('w-full border-collapse text-left text-xs', className)}>{children}</table>
    </div>
  );
}

export function PTh({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      scope="col"
      className={cn(
        'border-b border-platform-border bg-platform-card-muted px-4 py-3 font-mono text-[11px] font-bold uppercase tracking-wider text-platform-muted',
        className,
      )}
      {...props}
    />
  );
}

export function PTd({ className, ...props }: HTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={cn('border-b border-platform-border px-4 py-3 align-middle text-platform-text', className)} {...props} />
  );
}

export function PTr({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn('transition-colors hover:bg-platform-card-muted', className)} {...props} />;
}

// ── KPI card ─────────────────────────────────────────────────────────────

export function PKpiCard({
  label,
  value,
  valueHint,
  icon,
  footer,
  className,
}: {
  label: string;
  value: ReactNode;
  valueHint?: ReactNode;
  icon?: ReactNode;
  footer?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-card border border-platform-border bg-platform-surface p-5 transition-colors hover:border-platform-border-strong',
        className,
      )}
    >
      <div className="mb-2 flex items-center justify-between text-xs text-platform-muted">
        <span className="font-mono text-[11px] font-semibold uppercase tracking-wider">{label}</span>
        {icon && (
          <div className="flex h-8 w-8 items-center justify-center rounded-card border border-platform-border bg-platform-card-muted text-platform-accent">
            {icon}
          </div>
        )}
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <span className="font-mono text-3xl font-bold tabular-nums text-platform-text">{value}</span>
        {valueHint}
      </div>
      {footer && (
        <div className="mt-3 flex items-center justify-between border-t border-platform-border pt-2.5 font-mono text-[11px] text-platform-muted">
          {footer}
        </div>
      )}
    </div>
  );
}

// ── Buttons ──────────────────────────────────────────────────────────────

type PButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent';

const P_BUTTON_VARIANTS: Record<PButtonVariant, string> = {
  primary: 'bg-white text-platform-bg hover:bg-slate-200',
  secondary: 'bg-platform-surface text-platform-text border border-platform-border hover:bg-platform-card-muted',
  ghost: 'bg-transparent text-platform-muted hover:bg-platform-surface hover:text-platform-text',
  danger: 'bg-alert text-white hover:opacity-90 disabled:opacity-40',
  accent: 'bg-accent text-accent-fg hover:opacity-90',
};

export const PButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: PButtonVariant; loading?: boolean }
>(function PButton({ className, variant = 'secondary', loading, disabled, children, ...props }, ref) {
  return (
    <button
      ref={ref}
      className={cn(
        'inline-flex h-9 items-center justify-center gap-1.5 rounded-tight px-3.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        P_BUTTON_VARIANTS[variant],
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <span aria-hidden className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-r-transparent" />
      )}
      {children}
    </button>
  );
});

// ── Form fields ──────────────────────────────────────────────────────────

interface PFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: ReactNode;
  error?: string;
}

export const PField = forwardRef<HTMLInputElement, PFieldProps>(function PField(
  { label, hint, error, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  return (
    <div className="flex flex-col gap-1.5">
      <LabelPrimitive.Root htmlFor={inputId} className="text-xs font-semibold text-platform-text">
        {label}
      </LabelPrimitive.Root>
      {hint && <p className="text-[11px] text-platform-muted">{hint}</p>}
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        className={cn(
          'h-9 rounded-tight border border-platform-border bg-platform-card-muted px-3 text-xs text-platform-text placeholder:text-platform-subtle focus:border-platform-accent focus:outline-none',
          error && 'border-alert',
          className,
        )}
        {...props}
      />
      {error && <p className="text-xs text-alert">{error}</p>}
    </div>
  );
});

interface PSelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}

export const PSelectField = forwardRef<HTMLSelectElement, PSelectFieldProps>(function PSelectField(
  { label, hint, className, id, children, ...props },
  ref,
) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  return (
    <div className="flex flex-col gap-1.5">
      <LabelPrimitive.Root htmlFor={selectId} className="text-xs font-semibold text-platform-text">
        {label}
      </LabelPrimitive.Root>
      {hint && <p className="text-[11px] text-platform-muted">{hint}</p>}
      <select
        ref={ref}
        id={selectId}
        className={cn(
          'h-9 cursor-pointer rounded-tight border border-platform-border bg-platform-card-muted px-3 text-xs text-platform-text focus:border-platform-accent focus:outline-none',
          className,
        )}
        {...props}
      >
        {children}
      </select>
    </div>
  );
});

// ── Dialogs ──────────────────────────────────────────────────────────────

export function PModal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-platform-bg/80 backdrop-blur-sm" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[min(94vw,34rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-card border border-platform-border bg-platform-surface shadow-card focus:outline-none">
          <div className="border-b border-platform-border bg-platform-card-muted px-6 py-4">
            <DialogPrimitive.Title className="text-sm font-semibold text-platform-text">{title}</DialogPrimitive.Title>
            {description && <DialogPrimitive.Description className="mt-1 text-xs text-platform-muted">{description}</DialogPrimitive.Description>}
          </div>
          {children && <div className="p-6">{children}</div>}
          {footer && <div className="flex justify-end gap-2 border-t border-platform-border bg-platform-card-muted px-6 py-4">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * Type-to-confirm destructive/high-blast-radius action dialog — the dark
 * counterpart to `components/ui/Dialog.tsx`'s `DestructiveConfirmDialog`,
 * used for the two exit-criteria confirmations this phase requires (tenant
 * tier/state change, device-code deactivate). `matchText` is the literal
 * string the operator must type; the confirm button stays disabled until
 * the input matches exactly (case-sensitive — a typo should not silently
 * pass).
 */
export function PConfirmDialog({
  open,
  onOpenChange,
  title,
  consequence,
  matchText,
  matchLabel,
  confirmLabel,
  onConfirm,
  loading,
  typedValue,
  onTypedValueChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  consequence: ReactNode;
  matchText: string;
  matchLabel?: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  loading?: boolean;
  typedValue: string;
  onTypedValueChange: (v: string) => void;
}) {
  const matches = typedValue === matchText;
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-platform-bg/80 backdrop-blur-sm" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[min(94vw,30rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-card border border-alert/30 bg-platform-surface shadow-card focus:outline-none">
          <div className="flex items-center gap-2.5 border-b border-alert/20 bg-alert-soft/10 px-6 py-4 text-alert">
            <Icon name="gavel" className="text-[22px]" />
            <DialogPrimitive.Title className="font-mono text-sm font-bold uppercase tracking-wider">
              {title}
            </DialogPrimitive.Title>
          </div>
          <div className="space-y-4 p-6 text-xs">
            <div className="rounded-tight border border-alert/25 bg-alert-soft p-3 leading-relaxed text-ink">
              {consequence}
            </div>
            <div className="space-y-1">
              <label className="block font-semibold text-platform-text">
                Type {matchLabel ?? <span className="rounded-tight border border-alert/40 bg-platform-card-muted px-1.5 py-0.5 font-mono font-bold text-alert">{matchText}</span>} to confirm:
              </label>
              <input
                autoComplete="off"
                value={typedValue}
                onChange={(e) => onTypedValueChange(e.target.value)}
                placeholder={matchText}
                className="h-9 w-full rounded-tight border border-platform-border bg-platform-card-muted px-3 font-mono text-xs text-platform-text placeholder:text-platform-subtle focus:border-alert focus:outline-none"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2.5 border-t border-platform-border bg-platform-card-muted px-6 py-4">
            <PButton variant="secondary" onClick={() => onOpenChange(false)} type="button">
              Cancel
            </PButton>
            <PButton variant="danger" disabled={!matches} loading={loading} onClick={onConfirm} type="button">
              {confirmLabel}
            </PButton>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
